(() => {
  class ProductLandingPage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-landing-form]');
      this.variantIdInput = this.querySelector('[data-landing-variant-id]');
      this.priceElement = this.querySelector('[data-landing-price]');
      this.comparePriceElement = this.querySelector('[data-landing-compare-price]');
      this.saleBadge = this.querySelector('[data-landing-sale-badge]');
      this.anchorPrice = this.querySelector('[data-landing-anchor-price]');
      this.submitButton = this.querySelector('[data-landing-submit]');
      this.submitLabel = this.querySelector('[data-landing-submit-label]');
      this.totalElement = this.querySelector('[data-landing-total]');
      this.errorElement = this.querySelector('[data-landing-error]');
      this.mainImage = this.querySelector('[data-landing-main-image]');
      this.stage = this.querySelector('[data-landing-stage]');
      this.mediaCounter = this.querySelector('[data-landing-current-media]');
      this.thumbnails = Array.from(this.querySelectorAll('[data-landing-thumb]'));
      this.addonRows = Array.from(this.querySelectorAll('[data-landing-addon]'));
      this.modal = this.querySelector('[data-landing-modal]');
      this.modalContent = this.querySelector('[data-landing-modal-content]');
      this.modalAddButton = this.querySelector('[data-landing-modal-add]');
      this.modalAddLabel = this.querySelector('[data-landing-modal-add-label]');
      this.modalPrice = this.querySelector('[data-landing-modal-price]');
      this.preorderLocked = this.dataset.preorderLocked === 'true';

      if (document.querySelector('cart-drawer')) {
        this.submitButton?.setAttribute('aria-haspopup', 'dialog');
      }

      const productJson = this.querySelector('[data-landing-product-json]');
      if (!productJson || !this.form) return;

      try {
        this.productData = JSON.parse(productJson.textContent);
      } catch (error) {
        console.warn('Landing product data could not be read.', error);
        return;
      }

      this.moneyFormat = this.productData.moneyFormat || '${{amount}}';
      this.currentVariant = this.productData.variants.find(
        (variant) => String(variant.id) === String(this.variantIdInput.value)
      ) || this.productData.variants[0];

      this.querySelectorAll('[data-landing-option]').forEach((input) => {
        input.addEventListener('change', () => this.updateVariant());
      });

      this.thumbnails.forEach((button) => {
        button.addEventListener('click', () => this.selectGalleryImage(button));
      });

      this.querySelector('[data-landing-previous]')?.addEventListener('click', () => this.changeGalleryImage(-1));
      this.querySelector('[data-landing-next]')?.addEventListener('click', () => this.changeGalleryImage(1));

      if (this.stage && this.thumbnails.length > 1) {
        let pointerStart = null;
        this.stage.addEventListener('pointerdown', (event) => {
          if (event.target.closest('button')) return;
          pointerStart = { x: event.clientX, y: event.clientY };
        });
        this.stage.addEventListener('pointerup', (event) => {
          if (!pointerStart) return;
          const distanceX = event.clientX - pointerStart.x;
          const distanceY = event.clientY - pointerStart.y;
          pointerStart = null;
          if (Math.abs(distanceX) > 45 && Math.abs(distanceX) > Math.abs(distanceY)) {
            this.changeGalleryImage(distanceX < 0 ? 1 : -1);
          }
        });
        this.stage.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.changeGalleryImage(-1);
          if (event.key === 'ArrowRight') this.changeGalleryImage(1);
        });
      }

      this.addonRows.forEach((row) => {
        const checkbox = row.querySelector('[data-landing-addon-checkbox]');
        if (checkbox) {
          checkbox.addEventListener('change', () => {
            this.updateTotal();
            if (this.currentModalRow === row) this.updateModalAddButton(row);
          });
        }
        const modalButton = row.querySelector('[data-landing-open-modal]');
        if (modalButton) {
          modalButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openAddonModal(row);
          });
        }
      });

      this.querySelector('[data-landing-modal-close]')?.addEventListener('click', () => this.closeAddonModal());
      this.modalAddButton?.addEventListener('click', () => this.addCurrentModalProduct());
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeAddonModal();
      });

      const showMoreButton = this.querySelector('[data-landing-show-more]');
      if (showMoreButton) {
        showMoreButton.addEventListener('click', () => {
          this.querySelectorAll('.vex-landing__set-row--more[hidden]').forEach((row) => {
            row.hidden = false;
          });
          showMoreButton.remove();
        });
      }

      this.form.addEventListener('submit', (event) => this.submitSet(event));
      this.restoreSubmitState();
      this.updateAddonVariants();
      this.updateTotal();
    }

    getSelectedOptions() {
      return this.productData.options.map((optionName, optionIndex) => {
        const fieldset = this.querySelector(`[data-option-position="${optionIndex}"]`);
        return fieldset?.querySelector('[data-landing-option]:checked')?.value
          || this.currentVariant?.options?.[optionIndex]
          || this.productData.variants[0]?.options?.[optionIndex]
          || '';
      });
    }

    updateVariant() {
      const selectedOptions = this.getSelectedOptions();
      const nextVariant = this.productData.variants.find((variant) => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });

      this.currentVariant = nextVariant || null;
      this.clearError();

      if (!nextVariant) {
        this.variantIdInput.value = '';
        this.submitButton.disabled = true;
        this.submitLabel.textContent = window.variantStrings?.unavailable || 'Unavailable';
        return;
      }

      this.variantIdInput.value = nextVariant.id;
      this.priceElement.textContent = this.formatMoney(nextVariant.price);
      this.anchorPrice.textContent = this.formatMoney(nextVariant.price);

      const onSale = Number(nextVariant.compare_at_price || 0) > Number(nextVariant.price);
      if (this.comparePriceElement) {
        this.comparePriceElement.hidden = !onSale;
        this.comparePriceElement.textContent = onSale ? this.formatMoney(nextVariant.compare_at_price) : '';
      }
      if (this.saleBadge) this.saleBadge.hidden = !onSale;

      this.restoreSubmitState();

      this.selectVariantMedia(nextVariant);
      this.updateAddonVariants();
      this.updateTotal();

      if (window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', nextVariant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    restoreSubmitLabel() {
      const addLabel = this.dataset.addLabel || 'Add to Bag';
      this.submitLabel.innerHTML = `${addLabel} · <span data-landing-total></span>`;
      this.totalElement = this.submitLabel.querySelector('[data-landing-total]');
    }

    restoreSubmitState() {
      if (!this.submitButton || !this.submitLabel) return;
      const available = Boolean(this.currentVariant?.available) && !this.preorderLocked;
      this.submitButton.disabled = !available;

      if (available) {
        this.restoreSubmitLabel();
      } else {
        this.submitLabel.textContent = this.dataset.soldOutLabel || window.variantStrings?.soldOut || 'Sold out';
        this.totalElement = null;
      }
    }

    getSelectedMaterial() {
      const materialName = (this.dataset.materialOption || 'Material').toLowerCase();
      const optionIndex = this.productData.options.findIndex(
        (name) => String(name).toLowerCase() === materialName
      );
      return optionIndex >= 0 ? this.getSelectedOptions()[optionIndex] : '';
    }

    updateAddonVariants() {
      const selectedMaterial = this.getSelectedMaterial();
      const materialName = (this.dataset.materialOption || 'Material').toLowerCase();

      this.addonRows.forEach((row) => {
        if (!row.addonData) {
          try {
            row.addonData = JSON.parse(row.querySelector('[data-landing-addon-json]').textContent);
          } catch (error) {
            row.classList.add('is-unavailable');
            return;
          }
        }

        const optionIndex = row.addonData.options.findIndex(
          (name) => String(name).toLowerCase() === materialName
        );
        let addonVariant = null;

        if (optionIndex >= 0 && selectedMaterial) {
          addonVariant = row.addonData.variants.find(
            (variant) => variant.available && variant.options[optionIndex] === selectedMaterial
          );
        }

        addonVariant = addonVariant || row.addonData.variants.find((variant) => variant.available);
        const checkbox = row.querySelector('[data-landing-addon-checkbox]');

        if (!addonVariant) {
          row.classList.add('is-unavailable');
          row.dataset.variantId = '';
          row.dataset.price = '0';
          if (checkbox) {
            checkbox.checked = false;
            checkbox.disabled = true;
          }
          return;
        }

        row.classList.remove('is-unavailable');
        row.dataset.variantId = addonVariant.id;
        row.dataset.price = addonVariant.price;
        if (checkbox) checkbox.disabled = false;

        const price = row.querySelector('[data-landing-addon-price]');
        if (price) price.textContent = `+ ${this.formatMoney(addonVariant.price)}`;

        const image = row.querySelector('.vex-landing__addon-image');
        const imageUrl = addonVariant.featured_image?.src || addonVariant.featured_media?.preview_image?.src;
        if (image && imageUrl) {
          image.removeAttribute('srcset');
          image.src = imageUrl;
        }
      });
    }

    updateTotal() {
      if (!this.currentVariant) return;
      let total = Number(this.currentVariant.price || 0);

      this.addonRows.forEach((row) => {
        const checkbox = row.querySelector('[data-landing-addon-checkbox]');
        if (checkbox?.checked && !checkbox.disabled) total += Number(row.dataset.price || 0);
      });

      if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);
    }

    openAddonModal(row) {
      if (!this.modal || !this.modalContent) return;
      const template = row.querySelector('[data-landing-addon-modal-template]');
      if (!template) return;

      this.currentModalRow = row;
      this.modalContent.replaceChildren(template.content.cloneNode(true));

      const material = this.getSelectedMaterial();
      const materialElement = this.modalContent.querySelector('[data-landing-modal-material]');
      if (materialElement) materialElement.textContent = material || 'Selected style';

      const checkbox = row.querySelector('[data-landing-addon-checkbox]');
      const unavailable = !row.dataset.variantId || checkbox?.disabled;
      this.updateModalAddButton(row);
      if (this.modalPrice) {
        this.modalPrice.textContent = unavailable
          ? (window.variantStrings?.soldOut || 'Sold out')
          : this.formatMoney(Number(row.dataset.price || 0));
      }

      if (typeof this.modal.showModal === 'function') {
        if (!this.modal.open) this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
    }

    closeAddonModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) {
        this.modal.close();
      } else {
        this.modal.removeAttribute('open');
      }
      this.currentModalRow = null;
    }

    updateModalAddButton(row) {
      if (!this.modalAddButton) return;
      const checkbox = row?.querySelector('[data-landing-addon-checkbox]');
      const unavailable = !row?.dataset.variantId || checkbox?.disabled;
      const added = Boolean(checkbox?.checked && !unavailable);

      this.modalAddButton.disabled = unavailable;
      this.modalAddButton.classList.toggle('is-added', added);
      if (this.modalAddLabel) {
        this.modalAddLabel.textContent = added
          ? (this.dataset.modalAddedLabel || 'ADDED ✓')
          : (this.dataset.modalAddLabel || 'ADD');
      }
    }

    addCurrentModalProduct() {
      if (!this.currentModalRow) return;
      const checkbox = this.currentModalRow.querySelector('[data-landing-addon-checkbox]');
      if (!checkbox || checkbox.disabled) return;
      checkbox.checked = true;
      this.updateTotal();
      this.updateModalAddButton(this.currentModalRow);
    }

    selectGalleryImage(button) {
      if (!this.mainImage || !button.dataset.imageSrc) return;
      this.thumbnails.forEach((thumb) => thumb.classList.remove('is-active'));
      button.classList.add('is-active');
      this.currentMediaIndex = this.thumbnails.indexOf(button);
      if (this.mediaCounter) this.mediaCounter.textContent = String(this.currentMediaIndex + 1);
      this.mainImage.classList.add('is-changing');
      const nextImage = new Image();
      nextImage.onload = () => {
        this.mainImage.removeAttribute('srcset');
        this.mainImage.src = button.dataset.imageSrc;
        this.mainImage.alt = button.dataset.imageAlt || '';
        this.mainImage.classList.remove('is-changing');
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };
      nextImage.onerror = () => this.mainImage.classList.remove('is-changing');
      nextImage.src = button.dataset.imageSrc;
    }

    changeGalleryImage(direction) {
      if (this.thumbnails.length < 2) return;
      const activeIndex = this.thumbnails.findIndex((thumb) => thumb.classList.contains('is-active'));
      const currentIndex = activeIndex >= 0 ? activeIndex : 0;
      const nextIndex = (currentIndex + direction + this.thumbnails.length) % this.thumbnails.length;
      this.selectGalleryImage(this.thumbnails[nextIndex]);
    }

    selectVariantMedia(variant) {
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (!mediaId) return;
      const thumb = this.querySelector(`[data-landing-thumb][data-media-id="${mediaId}"]`);
      if (thumb) this.selectGalleryImage(thumb);
    }

    async submitSet(event) {
      event.preventDefault();
      if (this.preorderLocked || !this.currentVariant?.available || this.submitButton.disabled) return;

      this.clearError();
      this.submitButton.disabled = true;
      this.submitLabel.textContent = this.dataset.addingLabel || 'Adding…';

      const mainProperties = {};
      const formData = new FormData(this.form);
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim()) mainProperties[match[1]] = value;
      });

      if (this.dataset.setGroup) mainProperties._landing_group = this.dataset.setGroup;

      const items = [{
        id: Number(this.currentVariant.id),
        quantity: 1,
        properties: mainProperties
      }];

      this.addonRows.forEach((row) => {
        const checkbox = row.querySelector('[data-landing-addon-checkbox]');
        if (!checkbox?.checked || checkbox.disabled || !row.dataset.variantId) return;
        items.push({
          id: Number(row.dataset.variantId),
          quantity: 1,
          properties: {
            _landing_set_addon: 'true',
            _landing_group: this.dataset.setGroup || ''
          }
        });
      });

      const themeCart = document.querySelector('cart-items');
      if (themeCart && typeof themeCart.addToCart === 'function') {
        const handleProductAdded = () => {
          this.submitButton.removeEventListener('theme:product:add-error', handleProductError);
          this.restoreSubmitState();
          this.updateTotal();
        };
        const handleProductError = (cartEvent) => {
          this.submitButton.removeEventListener('theme:product:add', handleProductAdded);
          const response = cartEvent.detail?.response || {};
          this.showError(response.description || response.message || 'Unable to add this set. Please try again.');
          this.restoreSubmitState();
          this.updateTotal();
        };

        this.submitButton.addEventListener('theme:product:add', handleProductAdded, { once: true });
        this.submitButton.addEventListener('theme:product:add-error', handleProductError, { once: true });
        if (window.a11y) window.a11y.lastElement = this.submitButton;
        themeCart.addToCart(items, this.submitButton);
        return;
      }

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });
        const result = await response.json();

        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this set.');

        const cartUrl = window.theme?.routes?.cart_url || `${root}cart`;
        window.location.assign(cartUrl);
      } catch (error) {
        this.showError(error.message || 'Unable to add this set. Please try again.');
        this.restoreSubmitState();
        this.updateTotal();
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.moneyFormat);

      const amount = Number(cents || 0) / 100;
      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        const parts = number.toFixed(precision).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };

      const token = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
      let value;
      switch (token) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(amount, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(amount, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(amount, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(amount, 2);
      }
      return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
    }
  }

  if (!customElements.get('product-landing-page')) {
    customElements.define('product-landing-page', ProductLandingPage);
  }
})();
